import React from "react";
import { useNavigate } from "react-router-dom";

const SecurityPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f5f7fa]" data-testid="security-policy-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-10">
          <button onClick={() => navigate(-1)} className="text-sm text-[#4a9eff] hover:underline mb-4 inline-block" data-testid="security-back-btn">
            &larr; Back
          </button>
          <h1 className="text-3xl font-bold text-[#1e3a5f] tracking-tight">Security Policy</h1>
          <p className="text-sm text-[#5a6577] mt-2">Effective Date: March 30, 2026</p>
        </div>

        <div className="bg-white rounded-xl border border-[#e4e8ee] p-8 space-y-8 text-[#3d4f63] text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Security Overview</h2>
            <p>Opturna uses administrative, technical, and physical safeguards designed to protect user information and connected financial data. Opturna follows a data-minimization approach and uses Plaid to securely connect user accounts to supported financial institutions.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Core Security Controls</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>Encryption in transit using HTTPS and TLS 1.2 or higher.</li>
              <li>Encryption at rest for stored application and database data using strong industry-standard encryption.</li>
              <li>Secure secret management for API keys, tokens, and environment variables.</li>
              <li>Role-based access controls so users can access only their own information and administrative access is limited to authorized personnel.</li>
              <li>Password hashing using a modern one-way algorithm (bcrypt).</li>
              <li>Application logging and monitoring for suspicious activity, failed access attempts, and operational events.</li>
              <li>Change-management controls for production access and deployments.</li>
              <li>Routine security patching and dependency maintenance.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Plaid-Specific Safeguards</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>Bank login credentials are entered into Plaid Link or the financial institution authentication flow, not directly into Opturna.</li>
              <li>Opturna does not store online banking usernames or passwords.</li>
              <li>Opturna stores and uses the minimum Plaid-linked data needed to provide authorized features.</li>
              <li>Users can disconnect linked institutions at any time from within product settings or by contacting support.</li>
              <li>Access tokens and linked items are revoked when no longer needed or when a user requests disconnection or account deletion.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Access Management</h2>
            <p>Administrative access to production systems is limited by business need. Personnel are granted only the minimum access necessary to perform their duties. Access is reviewed periodically and removed promptly when no longer required.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Incident Response</h2>
            <p>If Opturna becomes aware of a security incident affecting user data, Opturna will investigate promptly, contain the issue, assess impact, rotate or revoke credentials where appropriate, and notify affected users and partners when required by law or contract.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">User Responsibilities</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>Maintain a strong password and keep credentials confidential.</li>
              <li>Use secure devices and networks when accessing the platform.</li>
              <li>Report suspected unauthorized access to <a href="mailto:admin@opturnatech.com" className="text-[#4a9eff] hover:underline">admin@opturnatech.com</a> immediately.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Contact</h2>
            <div className="p-4 bg-[#f5f7fa] rounded-lg border border-[#e4e8ee]">
              <p>Security questions can be sent to: <a href="mailto:admin@opturnatech.com" className="text-[#4a9eff] hover:underline">admin@opturnatech.com</a></p>
              <p className="font-semibold text-[#1e3a5f] mt-1">Opturna Tech Inc.</p>
            </div>
          </section>
        </div>

        <p className="text-center text-xs text-[#8b95a5] mt-8">Opturna Tech Inc. All rights reserved.</p>
      </div>
    </div>
  );
};

export default SecurityPolicy;
